
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { HeaderMenu } from "@/components/navigation/HeaderMenu";
import { SidebarProvider } from "@/components/ui/sidebar";
import { SidebarToggle } from "@/components/layout/SidebarToggle";
import { Footer } from "@/components/layout/Footer";
import { useIsMobile } from "@/hooks/use-mobile";
import { AuthProvider } from "@/components/auth/AuthProvider";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import Index from "./pages/Index";
import DailyPlanner from "./pages/DailyPlanner";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import AuthConfirm from "./pages/AuthConfirm";
import ResetPassword from "./pages/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";

// Configure React Query to ensure consistent state across tabs
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 60 * 60 * 1000, // 1 hour (replaces cacheTime)
    },
  },
});

const AppContent = () => {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const isAuthPage = ['/login', '/forgot-password', '/reset-password'].includes(pathname);
  const isPrivacyPage = pathname === '/privacy-policy';
  
  // Add an event listener for storage events to sync state across tabs
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      // Only invalidate cache for specific key changes to avoid unnecessary reloads
      if (event.key === 'habits' || event.key === 'habit-tracker-categories') {
        // Instead of reloading the whole page, refresh the query cache
        queryClient.invalidateQueries();
      }
    };
    
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  
  return (
    <SidebarProvider defaultOpen={!isMobile}>
      <div className="min-h-screen w-full bg-background font-outfit flex flex-col">
        {!isAuthPage && (
          <HeaderMenu />
        )}
        <div className="flex-1">
          <Routes>
            <Route path="/" element={
              <ProtectedRoute>
                <Index />
              </ProtectedRoute>
            } />
            <Route path="/planner" element={
              <ProtectedRoute>
                <DailyPlanner />
              </ProtectedRoute>
            } />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/confirm" element={<AuthConfirm />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
        {!isAuthPage && (
          <SidebarToggle />
        )}
        <Footer />
        <Toaster />
        <Sonner />
      </div>
    </SidebarProvider>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <HashRouter>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </HashRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
